import React, { useCallback } from "react";
import { Col } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import {
	Button,
	FormContainer,
	FormContent,
	HookFormInputGroup,
	HookSelectFormGroup,
	TextWithLoader,
} from "components";
import { FormColumn, FormRow, LineDivider } from "components/layout";
import { DebounceTimeout, ZendeskTicketFormFields } from "globalConstants";
import { getFullName } from "helpers";
import { ZendeskTicketReasons, ZendeskTicketType } from "types";
import {
	maxLength150,
	maxLength4000,
	required,
	trimNormalizer,
	trimMidSentenceNormalizer,
} from "validators";

import styles from "./zendeskTickets.module.scss";

type AddZendeskTicketFormProps = {
	submit: (params: {
		data: ZendeskTicketType;
		applicationId: string;
	}) => void;
	isLoading: boolean;
	applicationId: string;
	applicationData?: {
		mid: string;
		dbaName: string;
		primaryAgent: any;
	};
	merchantData?: {
		underwriting_qa_assignee: {
			first_name: string;
			last_name: string;
		};
	};
	isAssignedToMe?: boolean;
};

const AddZendeskTicketForm = ({
	submit,
	isLoading,
	applicationId,
	applicationData,
	merchantData,
	isAssignedToMe,
}: AddZendeskTicketFormProps) => {
	const { Comment, Reason, Subject } = ZendeskTicketFormFields;
	const formMethods = useForm({
		mode: "onBlur",
		reValidateMode: "onChange",
		shouldUnregister: false,
		defaultValues: {
			[Comment.name]: "",
			[Reason.name]: "",
			[Subject.name]: "",
		},
	});
	const {
		formState,
		handleSubmit,
		register,
		formState: { errors },
	} = formMethods;

	const onSubmit = useCallback(
		values => {
			const mappedValues: ZendeskTicketType = {
				[Comment.name]: values?.[Comment.name],
				[Reason.name]: values?.[Reason.name]?.name,
				[Subject.name]: values?.[Subject.name],
			};

			submit({ data: mappedValues, applicationId });
		},
		[Comment.name, Reason.name, Subject.name, submit, applicationId],
	);

	const reasons = ZendeskTicketReasons.getOptions();

	const getDbaName = () => {
		if (applicationData?.mid) {
			return (
				<>
					<div>
						{applicationData?.mid} - <br />
					</div>
					<div>{applicationData?.dbaName}</div>
				</>
			);
		} else {
			return <div>{applicationData?.dbaName}</div>;
		}
	};

	const requesterName = getFullName(merchantData?.underwriting_qa_assignee);

	return (
		<FormProvider {...formMethods}>
			<FormContent>
				<FormContainer>
					<form onSubmit={handleSubmit(onSubmit)}>
						<FormRow>
							<FormColumn>
								<TextWithLoader
									label="Merchant DBA"
									value={getDbaName()}
									loading={isLoading}
									labelCol={4}
									valueCol={8}
									dataAutotest="merchantDba"
								/>
							</FormColumn>
							<FormColumn>
								<TextWithLoader
									label="Requester"
									value={requesterName}
									loading={isLoading}
									labelCol={4}
									valueCol={8}
									dataAutotest="requester"
								/>
							</FormColumn>
						</FormRow>
						<FormRow className="mt-1">
							<FormColumn>
								<TextWithLoader
									label="Agent Office"
									value={
										applicationData?.primaryAgent?.company
											?.name
									}
									loading={isLoading}
									labelCol={4}
									valueCol={8}
									dataAutotest="agentOffice"
								/>
							</FormColumn>
						</FormRow>
						<LineDivider type="tight" className="mb-3" />
						<FormRow>
							<FormColumn>
								<HookSelectFormGroup
									label={Reason.displayName}
									options={reasons}
									getOptionLabel={option =>
										option.displayName
									}
									getOptionValue={option => option.value}
									placeholder="Please Select"
									name={Reason.name}
									isSearchable={false}
									cacheOptions
									defaultOptions
									debounceTimeout={DebounceTimeout}
									blurLoading={isLoading}
									colLabel={{ sm: 4 }}
									colSelect={{ sm: 8 }}
									rules={{
										validate: {
											required,
										},
									}}
									showRequiredMark
									defaultValue={null}
									isDisabled={!isAssignedToMe}
								/>
							</FormColumn>
						</FormRow>
						<FormRow>
							<Col>
								<HookFormInputGroup
									name={Subject.name}
									register={register}
									label={Subject.displayName}
									placeholder="Enter Subject"
									labelClassName={styles.fullLabel}
									inputBlockClassName={styles.fullInput}
									colInput={10}
									colLabel={2}
									showRequiredMark
									blurLoading={isLoading}
									errors={errors}
									validator={{
										required,
										maxLength150,
									}}
									normalize={trimMidSentenceNormalizer}
									normalizeOnBlur={trimNormalizer}
									disabled={!isAssignedToMe}
								/>
							</Col>
						</FormRow>
						<FormRow>
							<Col>
								<HookFormInputGroup
									name={Comment.name}
									register={register}
									label={Comment.displayName}
									placeholder="Enter Comment"
									labelClassName={styles.fullLabel}
									inputBlockClassName={styles.fullInput}
									rows={3}
									colInput={10}
									colLabel={2}
									showRequiredMark
									as="textarea"
									blurLoading={isLoading}
									errors={errors}
									validator={{
										required,
										maxLength4000,
									}}
									normalize={trimMidSentenceNormalizer}
									normalizeOnBlur={trimNormalizer}
									disabled={!isAssignedToMe}
								/>
							</Col>
						</FormRow>
						<FormRow>
							<Col>
								<Button
									type="submit"
									disabled={
										!formState.isDirty || !isAssignedToMe
									}
									value="Save"
									isLoading={isLoading}
								/>
							</Col>
						</FormRow>
					</form>
				</FormContainer>
			</FormContent>
		</FormProvider>
	);
};
export default AddZendeskTicketForm;
